import { BfusdVaultReadAbi, Erc4626ReadAbi, TotalSupplyAbi } from '../abis'
import type { SavingsRegistryEntry } from '../registry'
import { BITFI_RATIO_SCALE } from './bitfiBfbtc'
import { ONE_E18, toBigInt, type SavingsReader } from './shared'

/**
 * hbfUSD / pbfUSD — real ERC-4626, plus the two things 4626 cannot express.
 *
 * The generic reader would get the share price and the totals right and then
 * be wrong about both ends of the position:
 *
 * **Deposits are capped in SHARES, not assets.** `supplyCap()` is a bound on
 * `totalSupply()` (10,000,000 hbfUSD and 2,000,000 pbfUSD when this was
 * written), and `maxDeposit()` does report the remaining room — but only for a
 * caller, so a row built off the generic path publishes nothing at all. The
 * headroom is converted to bfUSD here with the DEPOSIT ratio, not the
 * redemption one, because that is the price the cap is actually hit at.
 *
 * **`currentDepositRatio()` is a second price.** It is the current epoch's
 * `endRatio` while `currentRatio()` is the value vesting linearly towards it,
 * so a depositor is charged the END of the window and a redeemer is paid the
 * point on it — 102796800 against 102794267 on 2026-09-08. The gap is small
 * and structural (it is the epoch's whole yield), never rounding, and it means
 * `previewDeposit` may not be used to size an exit or the reverse. The row
 * publishes the REDEEMABLE price as `exchangeRate`, following the Yield Basis
 * and Wren precedent, and the deposit price as `fundamentalExchangeRate` so
 * the difference is visible rather than averaged away.
 *
 * **The exit is NOT free, and `redemptionFee()` says it is.** That getter reads
 * `(0, 0)`, but `_claimWithdrawals` pays the requester only the `amount` it
 * snapshotted and sends the record's `unvestedYield` to the protocol's
 * `feeReceiver`, lumped in with the redemption fee. Redeeming mid-epoch
 * therefore forfeits the REST of that epoch's yield — nothing on the contract
 * calls that a fee, and only a fork shows it (measured 2026-09-08: a 1,000
 * bfUSD redemption paid 999.976924, keeping 0.023 as unvested yield).
 *
 * The cost is a real number and it is knowable right now, so the row publishes
 * it: the gap between the two ratios IS the unvested slice, so
 * `withdrawFeeBps` is `(endRatio − currentRatio) / currentRatio`. It decays to
 * zero across the epoch and jumps back to a full epoch's yield (~7 bps at the
 * current rate) the moment a new one settles — which is the honest shape of it,
 * and much better than the silence an absent field would leave.
 *
 * Exits are otherwise `request-based`: `redeem` or `withdraw` books a record,
 * `settlementDelay()` epochs later `claimWithdrawals` pays.
 * `pendingWithdrawals()` is the bfUSD already committed to those records — it
 * is NOT withdrawable liquidity, so it is deliberately not reported as any.
 */
export const readerBitfiVault = (
  entry: SavingsRegistryEntry,
): SavingsReader => {
  return {
    calls: [
      { address: entry.address, name: 'totalAssets', params: [] },
      { address: entry.address, name: 'totalSupply', params: [] },
      { address: entry.address, name: 'currentRatio', params: [] },
      { address: entry.address, name: 'currentDepositRatio', params: [] },
      { address: entry.address, name: 'supplyCap', params: [] },
      { address: entry.address, name: 'paused', params: [] },
    ],
    abis: [
      Erc4626ReadAbi,
      TotalSupplyAbi,
      BfusdVaultReadAbi,
      BfusdVaultReadAbi,
      BfusdVaultReadAbi,
      BfusdVaultReadAbi,
    ],
    parse: ([assets, supply, ratio, depositRatio, cap, paused]) => {
      const totalAssets = toBigInt(assets)
      const totalSupply = toBigInt(supply)
      const currentRatio = toBigInt(ratio)
      if (totalAssets === undefined || totalSupply === undefined)
        return undefined
      if (currentRatio === undefined || currentRatio <= 0n) return undefined

      // Both ratios are on BitFi's fixed 1e8 scale — the SAME one bfBTC uses,
      // and NOT the share token's 1e6. Normalising by `decimals` instead reads
      // 102.79 underlying per share rather than 1.0279, a 100x overstatement
      // that a 6-decimal vault makes look almost plausible.
      const exchangeRate = (currentRatio * ONE_E18) / BITFI_RATIO_SCALE
      const depRatio = toBigInt(depositRatio)
      const fundamentalExchangeRate =
        depRatio !== undefined && depRatio > 0n
          ? (depRatio * ONE_E18) / BITFI_RATIO_SCALE
          : undefined

      const supplyCap = toBigInt(cap)
      const isPaused = paused === true
      // Headroom in SHARES → bfUSD at the deposit price. A cap already met is
      // a real `0`, distinct from an absent (uncapped) reading.
      let depositCapacity: bigint | undefined
      if (isPaused) {
        depositCapacity = 0n
      } else if (supplyCap !== undefined && supplyCap > 0n) {
        const headroomShares =
          supplyCap > totalSupply ? supplyCap - totalSupply : 0n
        const priceRatio = depRatio && depRatio > 0n ? depRatio : currentRatio
        depositCapacity = (headroomShares * priceRatio) / BITFI_RATIO_SCALE
      }

      // What redeeming RIGHT NOW forfeits, in bps of the payout — see the
      // header. `0` is the correct answer at the end of an epoch (the two
      // ratios have converged), and it is a real statement, not a default.
      const withdrawFeeBps =
        depRatio !== undefined && depRatio > currentRatio
          ? Number(((depRatio - currentRatio) * 10_000n) / currentRatio)
          : 0

      return {
        totalAssets,
        totalSupply,
        exchangeRate,
        fundamentalExchangeRate,
        depositCapacity,
        withdrawFeeBps,
      }
    },
  }
}
